import {useMemo, useState} from 'react';
import {CogIcon} from '@heroicons/react/outline';
import {Spinner} from '@tyrannosaurustech/ui';

import {ContentWrapper} from '../../../components/ContentWrapper';
import {TitleM, ParagraphS} from '../../../components/atoms/Typography';
import {InitialsAvatar} from '../../../components/atoms/Avatar';
import {Theme} from '../../../components/atoms/theme';
import {useGetVettingStatsQuery} from '../../../redux/reduxQuery/vettingApi';
import {ChartStyles, DeltaPill, Legend, Sparkline} from '../TeamActivity/charts';
import {computeRollingTrendWindow, fmtDateTime, rollingSumsAt} from '../TeamActivity/constants';
import {BASIS_OPTIONS, Basis, VettingStats, buildUserSeries, computeBasisWindow, fmtAge, statusLabel} from './constants';
import {GoalProgress, GoalStackedChart} from './charts';
import {ConfigModal} from './ConfigModal';

const initialsOf = (name: string) =>
  (name || '?')
    .split(' ')
    .map((s) => s.charAt(0))
    .slice(0, 2)
    .join('');

export default function Vetting() {
  const [basis, setBasis] = useState<Basis>('daily');
  const [configOpen, setConfigOpen] = useState(false);

  const basisOption = BASIS_OPTIONS.find((b) => b.key === basis)!;
  const win = useMemo(() => computeBasisWindow(basis), [basis]);

  const {data, isLoading} = useGetVettingStatsQuery({
    startDate: win.startDate,
    endDate: win.endDate,
  });
  const {data: prevData} = useGetVettingStatsQuery({
    startDate: win.prevStartDate,
    endDate: win.prevEndDate,
  });

  // 12-month rolling trend (replaces the "vs prior" percent in the by-member table, which
  // was too noisy at short ranges). Independent of the daily/weekly/monthly basis toggle.
  const trendWin = useMemo(() => computeRollingTrendWindow(), []);
  const {data: trendData} = useGetVettingStatsQuery({
    startDate: trendWin.startDate,
    endDate: trendWin.endDate,
  });
  const rollingTrends = useMemo(() => {
    const dayIndex: Record<string, number> = {};
    trendWin.dayList.forEach((d, i) => (dayIndex[d] = i));
    const dailyByUser: Record<string, number[]> = {};
    (trendData?.completions || []).forEach((row: any) => {
      const idx = dayIndex[row.day];
      if (idx === undefined) return;
      if (!dailyByUser[row.userId]) dailyByUser[row.userId] = trendWin.dayList.map(() => 0);
      dailyByUser[row.userId][idx] += row.count;
    });
    const map: Record<string, number[]> = {};
    Object.keys(dailyByUser).forEach((userId) => {
      map[userId] = rollingSumsAt(trendWin.dayList, dailyByUser[userId], trendWin.sampleDayKeys);
    });
    return map;
  }, [trendData, trendWin]);

  const stats: VettingStats | undefined = data;
  const goal = stats?.config.goals[basis] || 0;

  const series = useMemo(() => buildUserSeries(stats, win), [stats, win]);

  // Prior-window totals, restricted to the same people the page shows so
  // "vs prior" compares like with like
  const prevTotals = useMemo(() => {
    const trackedIds = new Set((stats?.trackedUsers || []).map((u: any) => u.id));
    const map: Record<string, number> = {};
    (prevData?.completions || []).forEach((row: any) => {
      if (trackedIds.size && !trackedIds.has(row.userId)) return;
      map[row.userId] = (map[row.userId] || 0) + row.count;
    });
    return map;
  }, [prevData, stats]);

  const totalCompleted = series.reduce((a, s) => a + s.total, 0);
  const prevTotal = Object.values(prevTotals).reduce((a, v) => a + v, 0);
  const currentBucketTotal = series.reduce((a, s) => a + (s.perBucket[win.bucketCount - 1] || 0), 0);
  const topVetter = [...series].sort((a, b) => b.total - a.total)[0];

  const pending = stats?.pending || [];
  const pendingTotal = pending.reduce((a, row) => a + row.count, 0);
  const oldestPending = pending.reduce<string | null>(
    (oldest, row) => (row.oldest && (!oldest || new Date(row.oldest) < new Date(oldest)) ? row.oldest : oldest),
    null,
  );
  const maxPendingCount = Math.max(...pending.map((row) => row.count), 1);

  const chartSeries = series.map((s) => ({
    key: s.userId,
    label: s.userName,
    color: s.color,
    values: s.perBucket,
  }));

  const completedStatusLabel = statusLabel(stats?.config.completedStatus || 'vetted');

  return (
    <ContentWrapper>
      <ChartStyles />
      <div className="flex flex-wrap items-end justify-between gap-4 pt-6 pb-4">
        <div>
          <TitleM style={{margin: 0}}>Vetting</TitleM>
          <ParagraphS style={{margin: '4px 0 0', color: '#6C6579'}}>
            Requests moved to “{completedStatusLabel}” by the vetting team, and what is still outstanding.
          </ParagraphS>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-300 bg-white p-[3px]">
            {BASIS_OPTIONS.map((option) => (
              <button
                key={option.key}
                onClick={() => setBasis(option.key)}
                aria-pressed={basis === option.key}
                className="rounded-md px-3 py-1 text-xs font-semibold"
                style={basis === option.key ? {background: '#F7E9FB', color: Theme.colors.primaries_000} : {color: '#6C6579'}}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setConfigOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
          >
            <CogIcon width={14} />
            Settings
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-24">
          <Spinner colorClassName="text-primary" />
        </div>
      ) : (
        <>
          {stats && !stats.config.trackedUserIds.length && (
            <div className="ta-fadeup mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-purple-200 bg-purple-50 px-4 py-3">
              <div className="text-sm text-gray-700">
                <span className="font-bold">No vetting team configured.</span> Showing everyone who completed vettings — pick your vetters
                and set goals to make this dashboard theirs.
              </div>
              <button
                onClick={() => setConfigOpen(true)}
                className="rounded-lg px-3 py-1.5 text-xs font-bold text-white"
                style={{background: Theme.colors.primaries_000}}
              >
                Configure
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 pb-5 lg:grid-cols-4">
            <div className="ta-card ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Completed · {basisOption.windowLabel}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-bold">{totalCompleted}</span>
                <DeltaPill current={totalCompleted} prev={prevTotal} />
              </div>
              <div className="text-xs text-gray-500">vs. the {basisOption.windowLabel.replace('last', 'prior')}</div>
            </div>
            <div className="ta-card ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">{basisOption.currentLabel}</div>
              <div className="mt-2">
                <GoalProgress value={currentBucketTotal} goal={goal} />
              </div>
              <div className="mt-1 text-xs text-gray-500">{goal > 0 ? `Team goal per ${basisOption.unit}` : 'no goal set'}</div>
            </div>
            <div className="ta-card ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Outstanding now</div>
              <div className="mt-1 text-2xl font-bold">{pendingTotal}</div>
              <div className="text-xs text-gray-500">{pendingTotal ? `Oldest waiting ${fmtAge(oldestPending)}` : 'nothing pending'}</div>
            </div>
            <div className="ta-card ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Top vetter · {basisOption.windowLabel}</div>
              <div className="mt-1 truncate text-2xl font-bold">{topVetter && topVetter.total > 0 ? topVetter.userName : '—'}</div>
              <div className="text-xs text-gray-500">
                {topVetter && topVetter.total > 0 ? `${topVetter.total} completed` : 'no completions yet'}
              </div>
            </div>
          </div>

          <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
              <div className="text-sm font-bold">Completed per {basisOption.unit}</div>
              {chartSeries.length > 1 && (
                <Legend
                  items={chartSeries.map((s) => ({
                    key: s.key,
                    label: s.label,
                    color: s.color,
                  }))}
                />
              )}
            </div>
            <GoalStackedChart bucketLabels={win.bucketLabels} series={chartSeries} goal={goal} goalLabel={`goal ${goal}`} />
          </div>

          <div className="grid gap-5 lg:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white lg:col-span-2">
              <div className="px-4 pt-4 pb-2 text-sm font-bold">By team member</div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-[10.5px] font-bold uppercase tracking-wider text-gray-400">
                      <th className="px-4 py-2">Member</th>
                      <th className="px-4 py-2 text-right">Completed</th>
                      <th className="px-4 py-2 text-right">{basisOption.currentLabel}</th>
                      <th className="px-4 py-2" title={`Completed per ${basisOption.unit}, most recent ${basisOption.buckets}`}>
                        {basisOption.trendLabel}
                      </th>
                      <th className="px-4 py-2" title="Rolling 30-day total, sampled weekly, last 12 months">
                        12-mo trend
                      </th>
                      <th className="px-4 py-2">Last completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {series.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">
                          No completed vettings in this window.
                        </td>
                      </tr>
                    )}
                    {series.map((s) => (
                      <tr key={s.userId} className="ta-row border-b border-gray-100 last:border-0">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <InitialsAvatar text={initialsOf(s.userName)} background={s.color} hover={s.color} size={36} />
                            <span className="text-sm font-semibold">{s.userName || 'Unknown user'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-base font-bold tabular-nums">{s.total}</td>
                        <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums">{s.perBucket[win.bucketCount - 1] || 0}</td>
                        <td className="px-4 py-3">
                          <Sparkline values={s.perBucket} color={s.color} />
                        </td>
                        <td className="px-4 py-3">
                          <Sparkline values={rollingTrends[s.userId] || []} color={s.color} />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">{s.lastAt ? fmtDateTime(s.lastAt) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="pb-1 text-sm font-bold">Outstanding by status</div>
              <div className="pb-3 text-xs text-gray-500">Requests currently awaiting vetting work.</div>
              {pending.length === 0 && (
                <div className="py-8 text-center text-sm text-gray-400">Nothing outstanding — the queue is clear.</div>
              )}
              {pending.map((row) => (
                <div key={row.status} className="mb-3 last:mb-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium">{statusLabel(row.status)}</span>
                    <span className="text-sm font-bold tabular-nums">{row.count}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <div
                      style={{
                        flex: 1,
                        height: 8,
                        borderRadius: 4,
                        background: '#F1EDF6',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        className="ta-mixseg"
                        style={{
                          width: `${(row.count / maxPendingCount) * 100}%`,
                          height: '100%',
                          borderRadius: 4,
                          background: Theme.colors.primaries_000,
                        }}
                      />
                    </div>
                    <span className="w-14 text-right font-mono text-[11px] text-gray-400">
                      {row.oldest ? `${fmtAge(row.oldest)} old` : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <ParagraphS style={{color: '#9A93A8', margin: '14px 2px 32px'}}>
            Completions come from the audit log (status changes into “{completedStatusLabel}”). Team, goals, and statuses are configurable
            in Settings.
          </ParagraphS>
        </>
      )}

      <ConfigModal isOpen={configOpen} onClose={() => setConfigOpen(false)} />
    </ContentWrapper>
  );
}
